/**
 * File: apps/qbench-cp/reactapp/src/shared/configs/normalizer/schemas.js
 *
 * Purpose: Defines normalizr entity schemas describing relationships between core LIMS entities.
 *
 * Scope: Entity normalization schemas for orders, samples, tests, assays, panels, and their
 *     related entities across the customer portal's data layer.
 *
 * Tags: customer-portal, field-config
 *
 * Overview: Declares `schema.Entity` instances for tags, sources, projects, contacts, customers,
 *     turnarounds, assay categories, accessioning types, and teams, then composes them into
 *     richer entities — assay (with tags/team/category), panel (with nested assays), order (with
 *     project/turnaround/tags/contact/customer), test (with assay/panel/turnaround), and sample
 *     (with project/tests/order/source/accessioning type). Assay, order, test, and sample each
 *     use a `processStrategy` from `./strategies` to reshape raw API data during normalization,
 *     and a circular sample/test reference is added via `test.define({ sample })`. The exported
 *     `schemas` map provides both singular and array forms for use with normalizr's `normalize`.
 *
 * Exports: schemas (default export) — object of normalizr Entity/array schemas keyed by entity
 *     name.
 */

import { schema } from 'normalizr';
import strategies from './strategies';
import { EntityType } from 'shared/constants/entityTypes';

const tag = new schema.Entity('tags');
const source = new schema.Entity('sources');
const project = new schema.Entity('projects');
const contact = new schema.Entity('contacts');
const customer = new schema.Entity('customers');
const turnaround = new schema.Entity('turnarounds');
const assayCategory = new schema.Entity('assayCategories');
const accessioningType = new schema.Entity('accessioningTypes');
const team = new schema.Entity('teams', {});

const assay = new schema.Entity(
  'assays',
  {
    tags: [tag],
    team,
    category: assayCategory,
  },
  {
    processStrategy: strategies.process[EntityType.ASSAY],
  }
);

const panel = new schema.Entity('panels', {
  panel_assays: [{ assay }],
});

const order = new schema.Entity(
  'orders',
  {
    project,
    turnaround,
    tags: [tag],
    last_updated_by: contact,
    customer_account: customer,
  },
  {
    processStrategy: strategies.process[EntityType.ORDER],
  }
);

const test = new schema.Entity(
  'tests',
  {
    assay,
    panel,
    turnaround,
  },
  {
    processStrategy: strategies.process[EntityType.TEST],
  }
);

const sample = new schema.Entity(
  'samples',
  {
    project,
    tests: [test],
    order,
    source,
    accessioning_type: accessioningType,
  },
  {
    processStrategy: strategies.process[EntityType.SAMPLE],
  }
);

// Adds sample-test circular references.
test.define({ sample });

const schemas = {
  order,
  orders: [order],
  sample,
  samples: [sample],
  test,
  tests: [test],
  assay,
  assays: [assay],
  panel,
  turnarounds: [turnaround],
};

export default schemas;
